import React from 'react';
import { InterPoint } from '@saviynt/common';
import { Chip, type ChipColorTypes, Tooltip } from '@saviynt/design-system';

import { useInvertedLocalize } from '../../hooks';

type SeverityLevel = 'LOW' | 'MEDIUM' | 'MODERATE' | 'HIGH' | 'CRITICAL';

const RISK_PRIORITY_MAP = {
  LOW: { text: 'Low', color: 'success' },
  MEDIUM: { text: 'Medium', color: 'warning' },
  MODERATE: { text: 'Moderate', color: 'warning' },
  HIGH: { text: 'High', color: 'orange' },
  CRITICAL: { text: 'Critical', color: 'critical' },
} satisfies Record<SeverityLevel, { text: string; color: ChipColorTypes }>;

interface RiskPriorityChipProps {
  priority: string | null;
  riskScore: number | null;
  isPrefixInterpoint?: boolean;
  isOnlyLabel?: boolean;
  isDashReturn?: boolean;
  isMediumRiskAsModerate?: boolean;
}

/**
 * For risk priority chip
 */
function SodRiskPriorityChip({
  priority,
  riskScore,
  isPrefixInterpoint,
  isOnlyLabel,
  isDashReturn,
  isMediumRiskAsModerate = true,
}: RiskPriorityChipProps) {
  const l = useInvertedLocalize();

  if (!priority || !Object.keys(RISK_PRIORITY_MAP).includes(priority)) {
    return isDashReturn ? '-' : null;
  }

  const label = `${RISK_PRIORITY_MAP[priority as SeverityLevel].text}`;
  const { color } = RISK_PRIORITY_MAP[priority as SeverityLevel];

  const newLabel =
    Boolean(isMediumRiskAsModerate) && priority === 'MEDIUM'
      ? RISK_PRIORITY_MAP.MODERATE.text
      : label;

  const getRiskScore = () => {
    if (riskScore === null || riskScore === 0 || isOnlyLabel) {
      return l(newLabel);
    }

    return l(`${newLabel} ({riskScore})`, { riskScore });
  };

  return (
    <>
      <Tooltip
        trigger={
          <Chip
            size='xSmall'
            kind='subtleFilled'
            label={getRiskScore()}
            color={color}
          />
        }
        text=''
        placement='bottom'
      />
      {isPrefixInterpoint && <InterPoint color='neutralBold' font='body2' />}
    </>
  );
}

SodRiskPriorityChip.defaultProps = {
  isPrefixInterpoint: true,
  isOnlyLabel: false,
  isDashReturn: false,
  isMediumRiskAsModerate: true,
};

export default SodRiskPriorityChip;
